// src/pages/Appli/AppliAdmin/PrestationsAdmin/PrestationsAdmin.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  getPrestations,
  deletePrestation,
} from "./../../../../services/prestationsServices";

import Button from "./../../../../components/Button/Button";
import ButtonEdit from "./../../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../../components/Button/ButtonDelete";

import CreatePrestationModal from "./CreatePrestationModal";
import EditPrestationModal from "./EditPrestationModal";

import { useNotification } from "./../../../../components/Notification/NotificationManager";
import { FaPlus, FaGlobeEurope } from "react-icons/fa";
import { LanguageContext } from "./../../../../contexts/LanguageContext";
import "react-quill/dist/quill.snow.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const sanitize = (html) => DOMPurify.sanitize(String(html || ""));

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("fr")) return "fr";
  if (v.startsWith("en")) return "en";
  return "fr";
};

const stripHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = String(html || "");
  return (div.textContent || div.innerText || "").trim();
};

const PrestationCard = React.memo(function PrestationCard({
  prestation,
  lang,
  ui,
  onEdit,
  onDelete,
  onOpenLightbox,
}) {
  const titleKey = `prestation_title_${lang}`;
  const contentKey = `prestation_content_${lang}`;

  const title = prestation?.[titleKey] || "Titre indisponible";
  const content = prestation?.[contentKey] || "<p>Contenu indisponible</p>";

  const plain = stripHtml(content);
  const excerpt = plain.length > 220 ? `${plain.slice(0, 220)}…` : plain;

  const imgUrl = toAbs(prestation?.prestation_image);
  const ordre =
    prestation?.prestation_ordre === null || prestation?.prestation_ordre === undefined
      ? "—"
      : String(prestation.prestation_ordre);

  return (
    <article className={`${ui.card} overflow-hidden`}>
      {/* Image */}
      <div className="p-4">
        <div
          className="group relative rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 overflow-hidden cursor-pointer"
          onClick={() => imgUrl && onOpenLightbox(imgUrl)}
          role="button"
          tabIndex={0}
        >
          <div className="aspect-[16/10] w-full flex items-center justify-center">
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={title}
                className="h-full w-full object-contain"
                loading="lazy"
              />
            ) : (
              <div className={`p-10 text-center text-sm ${ui.muted}`}>
                Aucune image
              </div>
            )}
          </div>

          {/* Hover hint */}
          {imgUrl && (
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-black/20 flex items-end justify-end p-3">
              <span className="inline-flex items-center rounded-2xl px-3 py-2 text-sm font-bold bg-white/85 text-gray-900">
                Agrandir
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="px-5 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className={`text-lg sm:text-xl font-extrabold ${ui.title} leading-snug`}>
              {title}
            </h3>

            <div className="mt-2 flex flex-wrap gap-2">
              <span className={ui.chipSmall}>
                ID&nbsp;#{prestation?.id}
              </span>
              <span className={ui.chipSmall}>
                Ordre&nbsp;: <span className="font-extrabold text-primary ml-1">{ordre}</span>
              </span>
              <span className={ui.chipSmall}>
                {lang.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <ButtonEdit onClick={onEdit} />
            <ButtonDelete onClick={onDelete} />
          </div>
        </div>

        <div className="mt-4 rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4 relative overflow-hidden">
  <div className="ql-snow">
    <div
      className={`ql-editor p-0 text-sm leading-relaxed ${ui.muted} dark:text-gray-200`}
      style={{ maxHeight: 180, overflow: "hidden" }}
      dangerouslySetInnerHTML={{
        __html: sanitize(prestation?.[contentKey] || "<p>Contenu indisponible</p>"),
      }}
    />
  </div>

  {/* petit fondu en bas pour indiquer qu'il y a plus */}
  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white/90 to-transparent dark:from-slate-900/90" />
</div>

      </div>
    </article>
  );
});

const PrestationsAdmin = () => {
  const { addNotification } = useNotification();
  const { language } = useContext(LanguageContext);

  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(null);

  const [lightboxImg, setLightboxImg] = useState(null);

  // langue locale fiable (fr/en)
  const [localLanguage, setLocalLanguage] = useState(() => normLang(language));
  useEffect(() => {
    setLocalLanguage(normLang(language));
  }, [language]);

  const ui = useMemo(() => {
    const wrap = "max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-700 dark:text-gray-300";

    const card =
      "rounded-3xl border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm " +
      "dark:bg-white/5 dark:border-white/10 dark:shadow-none";

    const chipSmall =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white";

    const btnPrimary =
      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold " +
      "bg-primary text-white shadow hover:opacity-95 active:scale-[0.99] transition";

    const btnGhost =
      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "hover:bg-white dark:hover:bg-black/30 transition";

    const input =
      "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "focus:ring-2 focus:ring-primary focus:border-transparent";

    return { wrap, title, muted, card, chipSmall, btnPrimary, btnGhost, input };
  }, []);

  const fetchPrestations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPrestations();

      const sorted = (response || [])
        .slice()
        .sort((a, b) => {
          const oa = a?.prestation_ordre ?? 999999;
          const ob = b?.prestation_ordre ?? 999999;
          if (oa !== ob) return oa - ob;
          return (a?.id ?? 0) - (b?.id ?? 0);
        });

      setPrestations(sorted);
    } catch (error) {
      console.error("Erreur lors de la récupération des prestations :", error);
      addNotification("Erreur lors du chargement des prestations.", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchPrestations();
  }, [fetchPrestations]);

  const handleDelete = useCallback(
    async (id) => {
      const ok = window.confirm("Supprimer cette prestation ?");
      if (!ok) return;

      try {
        await deletePrestation(id);
        await fetchPrestations();
        addNotification("Prestation supprimée avec succès.", "success");
      } catch (error) {
        console.error("Erreur lors de la suppression de la prestation :", error);
        addNotification("Erreur lors de la suppression de la prestation.", "error");
      }
    },
    [addNotification, fetchPrestations]
  );

  return (
    <div className="relative min-h-screen">
      {/* fond léger */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent dark:from-black/70" />
      </div>

      <div className={`${ui.wrap} relative z-10 pt-10 pb-12`}>
        {/* Header */}
        <div className={`${ui.card} p-5 sm:p-6`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold ${ui.title}`}>
                Gestion des Prestations
              </h1>
              <p className={`mt-1 text-sm ${ui.muted}`}>
                Gère tes prestations, leur ordre, leur image et le contenu par langue.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${ui.muted} inline-flex items-center gap-2`}>
                  <FaGlobeEurope className="text-primary" />
                  Langue
                </span>
                <select
                  value={localLanguage}
                  onChange={(e) => setLocalLanguage(normLang(e.target.value))}
                  className={ui.input}
                  style={{ width: 140 }}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>

              {/* Tu peux garder ton Button component */}
              <Button
                text={
                  <span className="inline-flex items-center gap-2">
                    <FaPlus /> Créer
                  </span>
                }
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-2 sm:mt-0"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className={ui.chipSmall}>
              Total : <span className="ml-2 font-extrabold text-primary">{prestations.length}</span>
            </span>
            <span className={ui.chipSmall}>
              Affichage : {localLanguage.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className={`${ui.card} p-6`}>
              <p className={`text-center ${ui.muted}`}>Chargement…</p>
            </div>
          ) : prestations.length === 0 ? (
            <div className={`${ui.card} p-6`}>
              <p className={`text-center ${ui.muted}`}>Aucune prestation.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {prestations.map((p) => (
                <PrestationCard
                  key={p.id}
                  prestation={p}
                  lang={localLanguage}
                  ui={ui}
                  onEdit={() => setEditModalData(p)}
                  onDelete={() => handleDelete(p.id)}
                  onOpenLightbox={(img) => setLightboxImg(img)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modales */}
      {isCreateModalOpen && (
        <CreatePrestationModal
          language={localLanguage}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={async () => {
            await fetchPrestations();
          }}
        />
      )}

      {editModalData && (
        <EditPrestationModal
          prestation={editModalData}
          language={localLanguage}
          onClose={() => setEditModalData(null)}
          onUpdate={async () => {
            await fetchPrestations();
          }}
        />
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <img
            src={lightboxImg}
            alt="Agrandissement"
            className="max-h-[90vh] max-w-[92vw] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PrestationsAdmin;
